import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import EditOrderModal from './EditOrderModal';

function Orders() {
  const { schoolId } = useParams();
  const apiBase = `/api/admin/${schoolId}`;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingOrder, setEditingOrder] = useState(null);

  // Filters are derived from (and kept in sync with) URL search params so
  // they survive refresh and can be bookmarked / shared.
  const filters = {
    name: searchParams.get('name') || '',
    grade: searchParams.get('grade') || '',
    paid: searchParams.get('paid') || 'all',
    referenceNumber: searchParams.get('ref') || '',
  };

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBase}/orders`);
      setOrders(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load orders');
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${apiBase}/orders/${orderId}/status`, { status: newStatus });
      // Refresh orders
      fetchOrders();
    } catch (err) {
      alert('Failed to update order status');
    }
  };

  const updateOrderPayment = async (orderId, paid) => {
    try {
      await axios.put(`${apiBase}/orders/${orderId}/payment`, { paid: paid });
      // Refresh orders
      fetchOrders();
    } catch (err) {
      alert('Failed to update payment status');
    }
  };

  const deleteOrder = async (orderId, orderDetails) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this order?\n\n` +
      `Order: ${orderDetails.reference_number}\n` +
      `Name: ${orderDetails.name}\n` +
      `Grade: ${orderDetails.grade}\n\n` +
      `The order will be archived and hidden from the active orders list.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`${apiBase}/orders/${orderId}`);
      // Refresh orders
      fetchOrders();
    } catch (err) {
      alert('Failed to delete order');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
      ' ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'ordered': return 'status-pending';
      case 'printed': return 'status-ready';
      default: return 'status-pending';
    }
  };

  // Distinct grades actually present in the loaded orders, for the filter
  // dropdown. Derived from the data rather than hardcoded so it adapts to
  // whichever school(s)' grade options appear (see schools.yaml) without
  // code changes here.
  const availableGrades = Array.from(new Set(orders.map(o => o.grade).filter(Boolean))).sort();

  // Filter orders based on current filters
  const filteredOrders = orders.filter(order => {
    // Name filter
    if (filters.name && !order.name?.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }

    // Grade filter
    if (filters.grade && order.grade !== filters.grade) {
      return false;
    }

    // Reference number filter
    if (filters.referenceNumber && !order.reference_number?.toLowerCase().includes(filters.referenceNumber.toLowerCase())) {
      return false;
    }

    // Payment filter
    if (filters.paid === 'paid' && !order.paid) {
      return false;
    }
    if (filters.paid === 'unpaid' && order.paid) {
      return false;
    }

    return true;
  });

  // Map logical filter field names to the URL param keys used in the address bar.
  const filterParamKey = { name: 'name', grade: 'grade', paid: 'paid', referenceNumber: 'ref' };

  const handleFilterChange = (field, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const key = filterParamKey[field];
      // Remove the param entirely when it holds its default value so URLs stay
      // clean when no filtering is active.
      const isDefault = value === '' || (field === 'paid' && value === 'all');
      if (isDefault) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    }, { replace: true });
  };

  const clearFilters = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      ['name', 'grade', 'paid', 'ref'].forEach(k => next.delete(k));
      return next;
    }, { replace: true });
  };

  const toggleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(filteredOrders.map(order => order.id)));
    }
  };

  const bulkUpdatePayment = async (paid) => {
    if (selectedOrders.size === 0) {
      alert('Please select orders first');
      return;
    }

    const confirmed = window.confirm(
      `Mark ${selectedOrders.size} selected order(s) as ${paid ? 'paid' : 'unpaid'}?`
    );

    if (!confirmed) return;

    try {
      await Promise.all(
        Array.from(selectedOrders).map(orderId =>
          axios.put(`${apiBase}/orders/${orderId}/payment`, { paid })
        )
      );
      setSelectedOrders(new Set());
      fetchOrders();
    } catch (err) {
      alert('Failed to update some orders');
    }
  };

  const bulkUpdateStatus = async (status) => {
    if (selectedOrders.size === 0) {
      alert('Please select orders first');
      return;
    }

    const confirmed = window.confirm(
      `Mark ${selectedOrders.size} selected order(s) as ${status}?`
    );

    if (!confirmed) return;

    try {
      await Promise.all(
        Array.from(selectedOrders).map(orderId =>
          axios.put(`${apiBase}/orders/${orderId}/status`, { status })
        )
      );
      setSelectedOrders(new Set());
      fetchOrders();
    } catch (err) {
      alert('Failed to update some orders');
    }
  };

  const bulkDelete = async () => {
    if (selectedOrders.size === 0) {
      alert('Please select orders first');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedOrders.size} selected order(s)?\n\nThe orders will be archived and hidden from the active orders list.`
    );

    if (!confirmed) return;

    try {
      await Promise.all(
        Array.from(selectedOrders).map(orderId =>
          axios.delete(`${apiBase}/orders/${orderId}`)
        )
      );
      setSelectedOrders(new Set());
      fetchOrders();
    } catch (err) {
      alert('Failed to delete some orders');
    }
  };

  if (loading) return <div>Loading orders...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="orders-list">
      <div style={{
        marginBottom: '1rem'
      }}>
        <h2 style={{margin: 0}}>View Orders</h2>
      </div>

      {/* Filter Controls */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        border: '1px solid #dee2e6'
      }}>
        <h4 style={{margin: '0 0 1rem 0'}}>Filter Orders</h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          alignItems: 'end'
        }}>
          {/* Name Filter */}
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold'}}>
              Name:
            </label>
            <input
              type="text"
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              placeholder="Search by name..."
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            />
          </div>

          {/* Reference Number Filter */}
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold'}}>
              Reference #:
            </label>
            <input
              type="text"
              value={filters.referenceNumber}
              onChange={(e) => handleFilterChange('referenceNumber', e.target.value)}
              placeholder="Search by reference..."
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            />
          </div>

          {/* Grade Filter */}
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold'}}>
              Grade:
            </label>
            <select
              value={filters.grade}
              onChange={(e) => handleFilterChange('grade', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '0.9rem',
                backgroundColor: 'white'
              }}
            >
              <option value="">All Grades</option>
              {availableGrades.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
          </div>

          {/* Payment Status Filter */}
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 'bold'}}>
              Payment Status:
            </label>
            <select
              value={filters.paid}
              onChange={(e) => handleFilterChange('paid', e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '0.9rem',
                backgroundColor: 'white'
              }}
            >
              <option value="all">All Orders</option>
              <option value="paid">Paid Only</option>
              <option value="unpaid">Unpaid Only</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          <div>
            <button
              onClick={clearFilters}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Filter Results Info */}
        <div style={{marginTop: '1rem', fontSize: '0.9rem', color: '#666'}}>
          Showing {filteredOrders.length} of {orders.length} orders
          {(filters.name || filters.referenceNumber || filters.grade || filters.paid !== 'all') && (
            <span> (filtered)</span>
          )}
          {selectedOrders.size > 0 && (
            <span style={{color: '#007bff', fontWeight: 'bold'}}> • {selectedOrders.size} selected</span>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedOrders.size > 0 && (
        <div style={{
          backgroundColor: '#e7f3ff',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #b3d9ff',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <span style={{fontWeight: 'bold', color: '#0066cc'}}>
            {selectedOrders.size} order(s) selected
          </span>
          <button
            onClick={() => bulkUpdatePayment(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Mark Paid
          </button>
          <button
            onClick={() => bulkUpdatePayment(false)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Mark Unpaid
          </button>
          <button
            onClick={() => bulkUpdateStatus('printed')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Mark Printed
          </button>
          <button
            onClick={() => bulkUpdateStatus('ordered')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Mark Unprinted
          </button>
          <button
            onClick={bulkDelete}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Delete Selected
          </button>
          <button
            onClick={() => setSelectedOrders(new Set())}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'white',
              color: '#666',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginLeft: 'auto'
            }}
          >
            Clear Selection
          </button>
        </div>
      )}

      {filteredOrders.length === 0 ? (
        <p>No orders found</p>
      ) : (
        <div style={{overflowX: 'auto'}}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6'}}>
                <th style={{padding: '12px', textAlign: 'center', fontWeight: 'bold', width: '40px'}}>
                  <input
                    type="checkbox"
                    checked={filteredOrders.length > 0 && selectedOrders.size === filteredOrders.length}
                    onChange={toggleSelectAll}
                    style={{cursor: 'pointer', width: '16px', height: '16px'}}
                  />
                </th>
                <th style={{padding: '12px', textAlign: 'left', fontWeight: 'bold'}}>Order</th>
                <th style={{padding: '12px', textAlign: 'left', fontWeight: 'bold'}}>Name</th>
                <th style={{padding: '12px', textAlign: 'left', fontWeight: 'bold'}}>Grade</th>
                <th style={{padding: '12px', textAlign: 'left', fontWeight: 'bold'}}>Sandwich</th>
                <th style={{padding: '12px', textAlign: 'left', fontWeight: 'bold'}}>Extras</th>
                <th style={{padding: '12px', textAlign: 'left', fontWeight: 'bold'}}>Payment</th>
                <th style={{padding: '12px', textAlign: 'center', fontWeight: 'bold'}}>Paid</th>
                <th style={{padding: '12px', textAlign: 'center', fontWeight: 'bold'}}>Status</th>
                <th style={{padding: '12px', textAlign: 'center', fontWeight: 'bold'}}>Actions</th>
                <th style={{padding: '12px', textAlign: 'left', fontWeight: 'bold'}}>Placed</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} style={{borderBottom: '1px solid #dee2e6', backgroundColor: selectedOrders.has(order.id) ? '#f0f8ff' : 'transparent'}}>
                  <td style={{padding: '12px', textAlign: 'center'}}>
                    <input
                      type="checkbox"
                      checked={selectedOrders.has(order.id)}
                      onChange={() => toggleSelectOrder(order.id)}
                      style={{cursor: 'pointer', width: '16px', height: '16px'}}
                    />
                  </td>
                  <td style={{padding: '12px', fontWeight: 'bold'}}>{order.reference_number}</td>
                  <td style={{padding: '12px'}}>
                    {order.status === 'printed' ? (
                      <div title="Printed orders can't be edited — mark it unprinted first">{order.name}</div>
                    ) : (
                      <button
                        onClick={() => setEditingOrder(order)}
                        title="Edit order"
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          color: '#007bff',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textAlign: 'left'
                        }}
                      >
                        {order.name}
                      </button>
                    )}
                    {order.email && (
                      <div style={{fontSize: '0.8rem', color: '#666', marginTop: '2px'}}>
                        {order.email}
                      </div>
                    )}
                  </td>
                  <td style={{padding: '12px'}}>
                    <div>{order.grade}</div>
                    {order.teacher && (
                      <div style={{fontSize: '0.8rem', color: '#666', marginTop: '2px'}}>
                        {order.teacher}
                      </div>
                    )}
                  </td>
                  <td style={{padding: '12px', fontSize: '0.9rem'}}>
                    <div>{order.bread}, {order.cheese}, {order.meat}</div>
                    {(order.condiments || order.toppings) && (
                      <div style={{color: '#666', fontSize: '0.8rem', marginTop: '2px'}}>
                        {order.condiments && `+ ${order.condiments}`}
                        {order.condiments && order.toppings && ', '}
                        {order.toppings && `+ ${order.toppings}`}
                      </div>
                    )}
                  </td>
                  <td style={{padding: '12px', fontSize: '0.9rem'}}>
                    <div>{order.cookie}</div>
                    <div style={{color: '#666', fontSize: '0.8rem'}}>{order.side}</div>
                  </td>
                  <td style={{padding: '12px'}}>{order.payment}</td>
                  <td style={{padding: '12px', textAlign: 'center'}}>
                    <span className={`order-status ${order.paid ? 'status-ready' : 'status-pending'}`} style={{
                      fontSize: '0.8rem',
                      padding: '4px 8px',
                      borderRadius: '12px'
                    }}>
                      {order.paid ? 'Paid' : 'Unpaid'}
                    </span>
                  </td>
                  <td style={{padding: '12px', textAlign: 'center'}}>
                    <span className={`order-status ${getStatusClass(order.status)}`} style={{
                      fontSize: '0.8rem',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      textTransform: 'capitalize'
                    }}>
                      {order.status}
                    </span>
                  </td>
                  <td style={{padding: '12px', textAlign: 'center'}}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center'}}>
                      {/* Status Actions - Only show the transition that can be made */}
                      <div style={{display: 'flex', gap: '4px'}}>
                        {order.status === 'ordered' && (
                          <button
                            onClick={() => updateOrderStatus(order.id, 'printed')}
                            style={{
                              padding: '4px 8px',
                              fontSize: '0.75rem',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              backgroundColor: '#17a2b8',
                              color: 'white'
                            }}
                          >
                            Mark Printed
                          </button>
                        )}
                        {order.status === 'printed' && (
                          <button
                            onClick={() => updateOrderStatus(order.id, 'ordered')}
                            style={{
                              padding: '4px 8px',
                              fontSize: '0.75rem',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              backgroundColor: '#6c757d',
                              color: 'white'
                            }}
                          >
                            Mark Unprinted
                          </button>
                        )}
                      </div>
                      {/* Payment Actions */}
                      <div style={{display: 'flex', gap: '4px'}}>
                        <button
                          onClick={() => updateOrderPayment(order.id, !order.paid)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: order.paid ? '#6c757d' : '#28a745',
                            color: 'white'
                          }}
                        >
                          {order.paid ? 'Mark Unpaid' : 'Mark Paid'}
                        </button>
                      </div>
                      {/* Delete Action */}
                      <div style={{display: 'flex', gap: '4px'}}>
                        <button
                          onClick={() => deleteOrder(order.id, {
                            reference_number: order.reference_number,
                            name: order.name,
                            grade: order.grade
                          })}
                          style={{
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: '#dc3545',
                            color: 'white'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </td>
                  <td style={{padding: '12px', fontSize: '0.8rem', color: '#666'}}>
                    {formatDate(order.created_at)}
                  </td>
                </tr>
              ))}

            </tbody>
          </table>
        </div>
      )}

      {editingOrder && (
        <EditOrderModal
          apiBase={apiBase}
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSaved={() => {
            setEditingOrder(null);
            fetchOrders();
          }}
        />
      )}
    </div>
  );
}

export default Orders;
